import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Box, Text, Expando } from '../../components/Boneyard';
import Link from '@g4/next-furniture/Link';
import { hook } from '@g4/automation-hooks';
import { TRAVEL_ADVISORY } from '../../../automationHooks.json';
import Markdown from '../../components/Markdown';
import { TrackClick } from '@g4/trackster';
import tracking from '@g4/g4-analytics';

import { useSSRQuery } from '../../graphql';
import useOnClickOutside from '../../hooks/useOnClickOutside';

import TRAVEL_ADVISORY_QUERY from './travelAdvisory/travelAdvisory.query.graphql';

import EXTERNAL_LINK_ICON from '../../images/ic-external-link-blue.svg';
import ALERT_ICON from '../../images/ic-alert-blue-outline.svg';
import CLOSE_ICON from '../../images/ic-close-blue.svg';

import { AllegiantStadiumBannerContext } from '../app/AllegiantStadiumBannerProvider';

const ExternalLinkIcon = styled.img.attrs({
  src: EXTERNAL_LINK_ICON,
  alt: '',
})`
  width: 16px;
  height: 16px;
  margin-left: ${props => props.theme.spacing(1)};
`;

const AlertIcon = styled.img.attrs({
  src: ALERT_ICON,
  alt: '',
})`
  width: 24px;
  height: 24px;
  margin-right: ${props => props.theme.spacing(2)};

  ${props => props.theme.query.md} {
    position: absolute;
    top: 50%;
    left: 20px;
    transform: translateY(-50%);
    margin-right: 0;
  }
`;

const CloseIcon = styled.a`
  width: 15px;
  height: 15px;
  position: absolute;
  background-image: url(${CLOSE_ICON});
  background-size: cover;
  top: 10px;
  right: 10px;
`;

const calculateContainerTop = (props) => {
  let top = 18;
  if (props.isFlightMarketError) top += 60;
  if (props.hasAgentBanner) top += 30;
  if (props.isPasswordUpdated) top += 45;
  if (props.isEmailUpdated) top += 45;
  if (props.isError) top += 40;
  if (props.offsetTopPosition) top += 2;
  return `${top}px`;
};

const FullWidthContainer = styled.div`
  width: 100vw;
  margin-left: -${(props) => props.theme.spacing(3)};
  z-index: ${(props) => props.theme.zIndex.travelAdvisory};
  ${(props) => props.theme.query.md} {
    height: 1px;
    position: absolute;
    left: 50%;
    top: ${(props) => calculateContainerTop(props)};
    transform: translateX(-50%);
    margin-left: 0;
  }
`;

const Alert = styled(Expando)`
  background: ${props => props.theme.colors.travelAdvisory.background};
  ${props => props.theme.query.md} {
    position: absolute;
    right: 0;
    top: 0;
    border-radius: 8px 0 0 8px;
    max-width: 500px;
    box-shadow: 0 3px 6px 0 rgba(38, 50, 56, 0.4);
  }
`;

const AlertTitle = styled.button.attrs({
  type: 'button',
})`
  text-transform: uppercase;
  position: relative;
  color: ${props => props.theme.colors.travelAdvisory.title};
  font-size: 18px;
  font-weight: bold;
  padding: ${props => props.theme.spacing(4)} 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  &:focus {
    outline: none;
  }
  ${props => props.theme.query.md} {
    justify-content: flex-start;
    text-align: left;
    padding: ${props => props.theme.spacing(4)}
      ${props => props.theme.spacing(9)} ${props => props.theme.spacing(4)}
      ${props => props.theme.spacing(12)};
  }
`;

const AlertContent = styled.div`
  padding: ${props => props.theme.spacing(4)};
  padding-top: 0;
  ${props => props.theme.query.md} {
    padding-right: ${props => props.theme.spacing(8)};
    padding-bottom: ${props => props.theme.spacing(6)};
    padding-left: ${props => props.theme.spacing(12)};
  }
`;

const FlexAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  text-align: center;
`;

const CenteredLinkWrapper = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing(3)};
  ${props => props.theme.query.md} {
    margin-top: ${props => props.theme.spacing(5)};
  }
`;

const TravelAdvisory = ({
  theme,
  isFlightMarketError,
  hasAgentBanner,
  isPasswordUpdated,
  isEmailUpdated,
  isError,
}) => {
  const [isAlertVisible] = useState(true);
  const [isAlertExpanded, setIsAlertExpanded] = useState(false);
  const toggleIsAlertExpanded = () =>
    setIsAlertExpanded((prevState) => !prevState);

  const alertRef = React.useRef();
  useOnClickOutside(alertRef, () => {
    setIsAlertExpanded(false);
  });

  const { loading, error, data } = useSSRQuery(TRAVEL_ADVISORY_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const { stadiumBannerData } = useContext(AllegiantStadiumBannerContext) || {};
  const { allegiantStadiumBannerData } = stadiumBannerData || {};
  // Refactor items when finalized with business for promos
  React.useEffect(() => {
    if (process.browser && !loading && data && data.notifications) {
      const impressionData = {
        creative: data.notifications.title,
        name: 'Travel Advisory Impression',
        position: 'slot 1-HomePage',
        id: '',
        items: [
          {
            item_id: '',
            item_name: '',
            coupon: '',
            discount: '',
            affiliation: 'WWW - Home',
            index: '',
            item_brand: 'G4',
            item_category: '',
            item_category2: '',
            item_category3: '',
            item_category4: '',
            item_category5: '',
            price: '',
            quantity: '',
            promotion_name: 'Travel Advisory Impression',
            promotion_id: '',
          },
        ],
      };
      tracking.trackPromoView(impressionData);
    }
  }, [data, loading]);

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  const { notifications } = data;

  const allegiantStadiumBanner =
    allegiantStadiumBannerData?.application?.merchandiseBanner;

  const linkUrl = window.location.href.slice(0, -1) + notifications.link.url;
  return (
    notifications && (
      <FullWidthContainer
        isFlightMarketError={isFlightMarketError}
        hasAgentBanner={hasAgentBanner}
        isPasswordUpdated={isPasswordUpdated}
        isEmailUpdated={isEmailUpdated}
        isError={isError}
        offsetTopPosition={Boolean(allegiantStadiumBanner)}
      >
        <Box fullwidth position="relative">
          <div ref={alertRef}>
            {isAlertVisible && (
              <Alert
                label={
                  <TrackClick
                    category="site_click_tracking"
                    action="Homepage"
                    label="Travel Alert"
                  >
                    <AlertTitle
                      onClick={toggleIsAlertExpanded}
                      {...hook(TRAVEL_ADVISORY.hook, 'trigger')}
                    >
                      <AlertIcon />
                      {notifications.title}
                      {isAlertExpanded && (
                        <CloseIcon {...hook(TRAVEL_ADVISORY.hook, 'close')} />
                      )}
                    </AlertTitle>
                  </TrackClick>
                }
                automationHook={TRAVEL_ADVISORY.hook}
                open={isAlertExpanded}
                a11yLabel="Travel Advisory"
                icon="NONE"
              >
                {isAlertExpanded && (
                  <AlertContent {...hook(TRAVEL_ADVISORY.hook, 'content')}>
                    <Text
                      size={14}
                      lh={1.5}
                      color={theme.colors.travelAdvisory.text}
                    >
                      <Markdown source={notifications.summary} />
                    </Text>
                    <CenteredLinkWrapper>
                      <Link href={notifications.link.url} target="_blank">
                        {({ anchorProps }) => (
                          <TrackClick
                            category="site_navigation_clicks"
                            action={linkUrl}
                            label="learn more"
                          >
                            <FlexAnchor
                              {...anchorProps}
                              {...hook(TRAVEL_ADVISORY.hook, 'link')}
                            >
                              <Text
                                size={14}
                                color={theme.colors.travelAdvisory.text}
                                weight={700}
                              >
                                {notifications.link.title}
                              </Text>
                              <ExternalLinkIcon />
                            </FlexAnchor>
                          </TrackClick>
                        )}
                      </Link>
                    </CenteredLinkWrapper>
                  </AlertContent>
                )}
              </Alert>
            )}
          </div>
        </Box>
      </FullWidthContainer>
    )
  );
};

TravelAdvisory.propTypes = {
  image: PropTypes.string,
  theme: PropTypes.object,
  isFlightMarketError: PropTypes.bool,
  hasAgentBanner: PropTypes.bool,
  isPasswordUpdated: PropTypes.bool,
  isEmailUpdated: PropTypes.bool,
  isError: PropTypes.bool,
};

export default withTheme(TravelAdvisory);
